import { Link } from "react-router-dom";
import {
  ArrowLeft, Undo2, Redo2, Monitor, Tablet, Smartphone, Eye, Save,
  Sparkles, Coins, Users, Split,
} from "lucide-react";

const DEVICE_BTNS = [
  { k: "desktop", Icon: Monitor, label: "Desktop" },
  { k: "tablet",  Icon: Tablet,  label: "Tablet" },
  { k: "mobile",  Icon: Smartphone, label: "Mobile" },
];

export function BuilderToolbar({
  page, wallet, saving, lastSavedAt,
  canUndo, canRedo, onUndo, onRedo,
  device, onDeviceChange,
  onAiOpen, onPreview, onPublish, onWaitlist,
  onCreateVariant, onToggleAb, onDeleteVariant,
}) {
  return (
    <div className="h-14 shrink-0 border-b border-[#E4E5E9] bg-white flex items-center gap-3 px-4">
      <Link to="/app/pages" className="inline-flex items-center gap-1.5 text-xs text-[#4A4D57] hover:text-[#0B5CFF]" data-testid="builder-back">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
        <span className="hidden sm:inline">All pages</span>
      </Link>

      <div className="h-6 w-px bg-[#E4E5E9]" />

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onUndo}
          disabled={!canUndo}
          className="h-8 w-8 rounded-md text-[#0B0D17] hover:bg-[#F1F3F7] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center"
          title="Undo (Ctrl/Cmd+Z)"
          data-testid="toolbar-undo"
        >
          <Undo2 className="w-4 h-4" strokeWidth={1.75} />
        </button>
        <button
          type="button"
          onClick={onRedo}
          disabled={!canRedo}
          className="h-8 w-8 rounded-md text-[#0B0D17] hover:bg-[#F1F3F7] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center"
          title="Redo (Ctrl/Cmd+Shift+Z)"
          data-testid="toolbar-redo"
        >
          <Redo2 className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </div>

      <div className="h-6 w-px bg-[#E4E5E9]" />

      <div className="flex items-center gap-1 rounded-lg bg-[#F1F3F7] p-1" data-testid="toolbar-device">
        {DEVICE_BTNS.map(({ k, Icon, label }) => (
          <button
            key={k}
            type="button"
            onClick={() => onDeviceChange(k)}
            className={"h-6 w-8 rounded-md inline-flex items-center justify-center transition-colors " +
              (device === k ? "bg-white text-[#0B0D17] shadow-sm" : "text-[#4A4D57] hover:text-[#0B0D17]")}
            title={label}
            data-testid={`toolbar-device-${k}`}
          >
            <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
          </button>
        ))}
      </div>

      <div className="flex-1 flex items-center justify-center">
        <div className="text-xs text-[#747680] flex items-center gap-2 truncate max-w-[380px]">
          <span className="truncate font-semibold text-[#0B0D17]">{page.title || "Untitled page"}</span>
          <span className="text-[#E4E5E9]">·</span>
          {saving ? (
            <span className="text-[#F59E0B]">Saving…</span>
          ) : lastSavedAt ? (
            <span className="text-[#10B981] inline-flex items-center gap-1"><Save className="w-3 h-3" strokeWidth={2} /> Auto-saved</span>
          ) : (
            <span className="text-[#747680]">Ready</span>
          )}
        </div>
      </div>

      <Link to="/app/credits" className="hidden sm:inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs text-[#0B0D17] hover:bg-[#F1F3F7]" title="Credits balance">
        <Coins className="w-3.5 h-3.5 text-[#F5A623]" strokeWidth={1.75} /> {wallet?.balance ?? "—"}
      </Link>

      <button
        type="button"
        onClick={onAiOpen}
        className="h-8 px-3 rounded-md bg-white border border-[#E4E5E9] hover:border-[#0B5CFF] text-xs font-semibold text-[#0B0D17] inline-flex items-center gap-1.5"
        title="AI generate (5 credits)"
        data-testid="toolbar-ai"
      >
        <Sparkles className="w-3.5 h-3.5 text-[#0B5CFF]" strokeWidth={1.75} />
        <span className="hidden md:inline">AI generate</span>
      </button>

      {page.variant_b ? (
        <div className="hidden md:inline-flex items-center gap-1 h-8 px-2 rounded-md bg-[#E6EEFF] text-[10px] text-[#0B0D17]">
          <Split className="w-3 h-3" strokeWidth={2} />
          A·{page.ab_stats?.a_views || 0} / B·{page.ab_stats?.b_views || 0}
          <button onClick={onToggleAb} className="ml-1 hover:underline font-semibold">
            {page.ab_enabled ? "pause" : "resume"}
          </button>
          <button onClick={onDeleteVariant} className="ml-0.5 text-[#B00020] hover:underline">×</button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onCreateVariant}
          className="h-8 px-2.5 rounded-md bg-white border border-[#E4E5E9] hover:border-[#0B5CFF] text-xs font-semibold text-[#0B0D17] inline-flex items-center gap-1.5"
          title="Create A/B variant"
          data-testid="toolbar-ab"
        >
          <Split className="w-3.5 h-3.5" strokeWidth={1.75} />
          <span className="hidden lg:inline">A/B test</span>
        </button>
      )}

      <button
        type="button"
        onClick={onWaitlist}
        className="h-8 px-2.5 rounded-md bg-white border border-[#E4E5E9] hover:border-[#0B5CFF] text-xs font-semibold text-[#0B0D17] inline-flex items-center gap-1.5"
        title="Waitlist"
        data-testid="toolbar-waitlist"
      >
        <Users className="w-3.5 h-3.5" strokeWidth={1.75} />
        <span className="hidden lg:inline">Waitlist</span>
      </button>

      <button
        type="button"
        onClick={onPreview}
        className="h-8 px-3 rounded-md bg-white border border-[#E4E5E9] hover:border-[#0B5CFF] text-xs font-semibold text-[#0B0D17] inline-flex items-center gap-1.5"
        data-testid="toolbar-preview"
      >
        <Eye className="w-3.5 h-3.5" strokeWidth={1.75} />
        Preview
      </button>

      <button
        type="button"
        onClick={onPublish}
        className={"h-8 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 " +
          (page.published
            ? "bg-white border border-[#E4E5E9] hover:border-[#0B5CFF] text-[#0B0D17]"
            : "bg-[#0B5CFF] text-white hover:bg-[#0B4FE3]")}
        data-testid="toolbar-publish"
      >
        {page.published ? "Unpublish" : "Publish"}
      </button>
    </div>
  );
}
